// 👇 Cambios clave:
// - Dos fuentes: allOrdersNotAssociated / allOrdersAssociated
// - Dos visibles: ordersNotAssociated / ordersAssociated
// - Filtros y paginaciones independientes
// - Carga inicial en paralelo y misma selección selectOrder(orderId) sirve para ambas tablas

import { Component, ElementRef, HostListener, ViewChild } from '@angular/core';
import { ToastrService } from 'ngx-toastr';
import { AuthenticationService } from '../../../authentication/authentication/authentication.service';
import { CompanyService } from 'src/app/services/company.service';
import { PurchaseOrderService } from 'src/app/services/purchase-order.service';
import { PurchasesService } from 'src/app/services/purchases.service';
import { ResponseObject } from 'src/app/models/response-object.model';
import { ButtonLoading } from 'src/app/models/button-loading.model';
import { SimplePaginationModel } from 'src/app/models/pagination.model';
import { HttpErrorResponse } from '@angular/common/http';
import { forkJoin, of } from 'rxjs';
import { map, catchError } from 'rxjs/operators';
import { getLongDate, getStringDate } from 'src/app/common/util';
import { PurchaseOrderDetailResponse } from 'src/app/models/purchase-order.model';
import { PurchasesAssociationService } from 'src/app/services/purchases-association.service';
import { PurchaseOrderReceivedService } from 'src/app/services/purchase-order-received.service';

type OrderVM = {
  id: number;
  number: string;
  providerName: string;
  date: number | string;
  total: number;
  enteredIntoInventory: boolean;
};

type PurchaseVM = {
  id: number;
  number: string;
  providerName: string;
  date: number | string;
  total: number;
  associatedOrderId?: number | null;
};

@Component({
  selector: 'app-order-association',
  templateUrl: './order-association.component.html',
  styleUrls: ['./order-association.component.scss']
})
export class OrderAssociationComponent {
  loading = false;
  loadingCost: { [key: number]: boolean } = {};

  // ======= Órdenes: separar fuentes / visibles =======
  allOrdersNotAssociated: OrderVM[] = [];
  allOrdersAssociated: OrderVM[] = [];

  ordersNotAssociated: OrderVM[] = [];
  ordersAssociated: OrderVM[] = [];

  filterOrdersNot = '';
  filterOrdersYes = '';

  paginationOrdersNot = new SimplePaginationModel(0, 5, 0, 0, 0);
  paginationOrdersYes = new SimplePaginationModel(0, 5, 0, 0, 0);

  private timeoutOrdersNot: any = null;
  private timeoutOrdersYes: any = null;

  // ======= Compras (sin cambios sustanciales) =======
  allPurchases: PurchaseVM[] = [];
  basePurchases: PurchaseVM[] = [];
  purchases: PurchaseVM[] = [];
  filterPurchases = '';
  paginationPurchases = new SimplePaginationModel(0, 5, 0, 0, 0);

  // Fechas
  startDate = '';
  endDate = '';

  // Selección / asociaciones
  selectedOrderId: number | null = null;
  initialRelatedPurchaseIdsBefore: number[] = [];
  initialRelatedPurchaseBefore = [];
  initialRelatedPurchaseIds: number[] = [];
  currentRelatedPurchaseIds: number[] = [];
  //editingItem: number | null = null;

  buttonAssociate: ButtonLoading = new ButtonLoading('btn-success', false, 'GUARDAR', 'fa-save');

  // Detalles
  currentOrderDetails: PurchaseOrderDetailResponse[];
  currentPurchases = [];
  loadingPurchaseDetails = false;

  private associatedPurchasesCache = new Map<number, PurchaseVM[]>();

  // SVG
  @ViewChild('svgContainer', { static: true }) svgContainer: ElementRef;
  @HostListener('window:resize') onResize() { this.drawMultipleLinks(); }

  currentReceivedOrderDetails: any;

  constructor(
    private toastService: ToastrService,
    private authService: AuthenticationService,
    private companyService: CompanyService,
    private purchaseOrderService: PurchaseOrderService,
    private purchasesAssociationService: PurchasesAssociationService,
    private purchasesService: PurchasesService,
    private purchaseOrderReceivedService: PurchaseOrderReceivedService
  ) {
    const today = new Date();
    this.startDate = getStringDate(today);
    this.endDate = getStringDate(today);
  }

  ngOnInit(): void {
    this.loadStartData();
  }

  /** Carga inicial (ambas tablas + compras) */
  loadStartData() {
    forkJoin({
      not: this.purchaseOrderService.getOrdersNotAssociated(),
      yes: this.purchaseOrderService.getOrdersAssociated(),
    }).subscribe({
      next: ({ not, yes }: any) => {
        // Normalizar y ordenar
        this.allOrdersNotAssociated = (not?.data || []).map((o: any) => this.normalizeOrder(o))
          .sort((a, b) => Number(b.number) - Number(a.number));
        this.allOrdersAssociated = (yes?.data || []).map((o: any) => this.normalizeOrder(o))
          .sort((a, b) => Number(b.number) - Number(a.number));

        this.refreshOrdersNotPagination();
        this.refreshOrdersYesPagination();

        // Compras
        this.reloadPurchases();
      },
      error: () => {
        this.toastService.error('Error al cargar órdenes', 'Error');
        // Aún así intenta cargar compras
        this.reloadPurchases();
      }
    });
  }

  // =================== Normalizadores ===================
  private normalizePurchase(p: any): PurchaseVM {
    const composedNumber = p.series ? `${p.series}-${p.number}` : String(p.number);
    return {
      id: p.id,
      number: composedNumber,
      providerName: p.providerData?.name ?? '—',
      date: p.date ?? p.accountingDate,
      total: p.totalCost ?? 0,
      associatedOrderId: p.associatedOrderId ?? null
    };
  }

  private normalizeOrder(o: any): OrderVM {
    return {
      id: o.id,
      number: String(o.number),
      providerName: o.providerData?.name ?? '—',
      date: o.creationDate,
      total: 0,
      enteredIntoInventory: o.enteredIntoInventory
    };
  }

  // =================== Compras ===================
  reloadPurchases() {
    const start = getLongDate(this.startDate);
    const end = getLongDate(this.endDate);

    this.purchasesService.getPurchasesByIntervalDate(start, end).subscribe({
      next: (resp: ResponseObject) => {
        this.basePurchases = (resp.data || []).map((p: any) => this.normalizePurchase(p));
        this.resetAllPurchasesToBase();

        if (this.selectedOrderId && this.currentRelatedPurchaseIds?.length) {
          const cached = this.associatedPurchasesCache.get(this.selectedOrderId) || [];
          if (cached.length) this.upsertAssociatedIntoAllPurchases(cached, this.selectedOrderId);

          const relatedSet = new Set(this.currentRelatedPurchaseIds);
          this.allPurchases = this.allPurchases.map(p => ({
            ...p,
            associatedOrderId: relatedSet.has(p.id) ? this.selectedOrderId : p.associatedOrderId ?? null
          }));
        }

        this.paginationPurchases.page = 0;
        this.refreshPurchasesPagination();
        this.drawMultipleLinks();
      },
      error: () => this.toastService.error('Error al obtener compras')
    });
  }

  // =================== Selección / detalles ===================
  selectOrder(orderId: number) {
    if (this.selectedOrderId === orderId) return;
    
    this.currentReceivedOrderDetails = null;

    this.selectedOrderId = orderId;
    this.getOrderDetails();
    this.getReceivedOrderDetails();

    this.purchasesAssociationService.getRelatedPurchasesByOrder(orderId).subscribe({
      next: (resp: ResponseObject) => {
        const relatedIds: number[] = (resp.data || []).map((item: any) => item.id);

        this.initialRelatedPurchaseBefore = [...resp.data];
        this.initialRelatedPurchaseIds = [...relatedIds];
        this.initialRelatedPurchaseIdsBefore = [...relatedIds];
        this.currentRelatedPurchaseIds = [...relatedIds];

        this.resetAllPurchasesToBase();
        this.upsertAssociatedIntoAllPurchases(resp.data || [], orderId);

        this.paginationPurchases.page = 0;
        this.refreshPurchasesPagination();
        this.getPurchaseDetails();
        this.drawMultipleLinks();
      },
      error: () => this.toastService.error('Error al obtener asociaciones de la orden')
    });
  }

  getOrderDetails() {
    if (!this.selectedOrderId) return;

    this.purchaseOrderService.getOrder(this.selectedOrderId).subscribe({
      next: (response: ResponseObject) => {
        this.currentOrderDetails = response.data;
      },
      error: () => {
        this.toastService.error('Error al obtener los detalles de la Orden de compra', 'Error');
      }
    });
  }

  getReceivedOrderDetails(){
    if (!this.selectedOrderId) return;

    this.purchaseOrderReceivedService.getReceivedOrderDetails(this.selectedOrderId).subscribe({
      next: (response: ResponseObject) => {
        this.currentReceivedOrderDetails = response.data;
      },
      error: () => {
        this.currentReceivedOrderDetails = null;
      }
    });
  }

  getPurchaseDetails() {
    this.currentPurchases = [];
    if (!this.currentRelatedPurchaseIds?.length) return;

    this.loadingPurchaseDetails = true;
    const calls = this.currentRelatedPurchaseIds.map(id =>
      this.purchasesService.getDetailPurchaseInvoice(id).pipe(
        map((resp: ResponseObject) => resp.data || []),
        catchError(() => of([]))
      )
    );

    forkJoin(calls).subscribe({
      next: (arrays) => {
        const joined = ([] as any[]).concat(...arrays).sort((a, b) => a.id - b.id);
        this.currentPurchases = joined;
        this.loadingPurchaseDetails = false;
      },
      error: () => {
        this.toastService.error('Error al obtener detalles de compras', 'Error');
        this.loadingPurchaseDetails = false;
      }
    });
  }

  // =================== Edición costo detalle ===================
  editingItem: number | null = null;
  startEditing(itemId: number) { this.editingItem = itemId; }
  endEditing() { this.editingItem = null; }

  updateCostOrderDetail(orderDetail: any) {
    if (!orderDetail) return;

    this.loadingCost[orderDetail.id] = true;
    this.purchaseOrderService
      .putOrderDetailCostToRecive(this.selectedOrderId, orderDetail.product, orderDetail.cost, orderDetail.toReceive)
      .subscribe({
        next: () => {
          this.getOrderDetails();
          this.editingItem = null;
          this.loadingCost[orderDetail.id] = false;
          this.toastService.success('Costo actualizado correctamente', 'Éxito');
        },
        error: () => {
          this.toastService.error('Error al actualizar el costo del detalle', 'Error');
          this.loadingCost[orderDetail.id] = false;
        }
      });
  }

  // =================== Asociaciones ===================
  isPurchaseLocked(p: PurchaseVM): boolean {
    if (!p.associatedOrderId) return false;
    return this.selectedOrderId != null && p.associatedOrderId !== this.selectedOrderId;
  }

  togglePurchase(p: PurchaseVM) {
    if (!this.selectedOrderId) return;

    if (this.isPurchaseLocked(p)) {
      this.toastService.warning('Esta compra ya está asociada a otra orden.');
      return;
    }

    const idx = this.currentRelatedPurchaseIds.indexOf(p.id);
    if (idx === -1) {
      this.currentRelatedPurchaseIds.push(p.id);
      p.associatedOrderId = this.selectedOrderId;
      this.getPurchaseDetails();
    } else {
      this.currentRelatedPurchaseIds.splice(idx, 1);
      if (p.associatedOrderId === this.selectedOrderId) p.associatedOrderId = null;
      this.getPurchaseDetails();
    }

    this.refreshPurchasesPagination();
    this.drawMultipleLinks();
  }

  getDisabledSave(): boolean {
    const initialSet = new Set(this.initialRelatedPurchaseIdsBefore);
    const currentSet = new Set(this.currentRelatedPurchaseIds);

    const toAdd = this.currentRelatedPurchaseIds.some(id => !initialSet.has(id));
    const toRemove = this.initialRelatedPurchaseIdsBefore.some(id => !currentSet.has(id));

    return !(toAdd || toRemove) || !this.selectedOrderId || this.buttonAssociate.loading;
  }

  saveAssociation(finalize: boolean) {
    if (!this.selectedOrderId) return;

    const orderId = this.selectedOrderId;
    const initialSet = new Set(this.initialRelatedPurchaseIdsBefore);
    const currentSet = new Set(this.currentRelatedPurchaseIds);

    const toAdd = Array.from(currentSet).filter(id => !initialSet.has(id));
    const toRemove = Array.from(initialSet).filter(id => !currentSet.has(id));

    if (!toAdd.length && !toRemove.length) {
      this.toastService.info('No hay cambios por guardar.');
      return;
    }

    this.buttonAssociate.loading = true;
    this.loading = true;

    const ops = [
      ...toAdd.map(pid => this.purchasesAssociationService.createRelationPurchaseOrder(orderId, pid)),
      ...toRemove.map(pid => this.purchasesAssociationService.deleteRelationPurchaseOrder(orderId, pid)),
    ];

    forkJoin(ops).subscribe({
      next: () => {
        this.toastService.success('Asociaciones actualizadas correctamente', 'Éxito');
        this.buttonAssociate.loading = false;
        this.updateInventoriesForOrder(orderId, finalize);
        // refrescar estado base (mantener tablas)
        this.selectOrder(orderId);
        this.reloadPurchases();
        this.reloadOrdersOnly(); // 👈 recarga ambas listas de órdenes
      },
      error: (e: any) => {
        console.log(e);
        
        this.toastService.error(e?.error?.data.description || 'Error al actualizar asociaciones', 'Error');
        this.buttonAssociate.loading = false;
        this.loading = false;
      }
    });
  }

  updateInventoriesForOrder(purchaseOrderId: number, finalized: boolean) {
    const data = { finalize: finalized };
    this.purchasesAssociationService.putInventoriesForOrder(purchaseOrderId, data).subscribe({
      next: (response: ResponseObject) => {
        if (response.data) this.toastService.success('Inventarios actualizados correctamente', 'Éxito');
        this.loading = false;
      },
      error: (e: HttpErrorResponse) => {
        this.toastService.error(e?.error?.message || 'Error al actualizar inventarios', 'Error');
        this.loading = false;
      }
    });
  }

  // =================== Paginación / filtros (órdenes) ===================
  debouncedFilterOrdersNot() {
    clearTimeout(this.timeoutOrdersNot);
    this.timeoutOrdersNot = setTimeout(() => {
      this.paginationOrdersNot.page = 0;
      this.refreshOrdersNotPagination();
    }, 250);
  }

  debouncedFilterOrdersYes() {
    clearTimeout(this.timeoutOrdersYes);
    this.timeoutOrdersYes = setTimeout(() => {
      this.paginationOrdersYes.page = 0;
      this.refreshOrdersYesPagination();
    }, 250);
  }

  public refreshOrdersNotPagination() {
    const q = (this.filterOrdersNot || '').toLowerCase();
    const filtered = this.allOrdersNotAssociated.filter(o =>
      (o.number || '').toLowerCase().includes(q) ||
      (o.providerName || '').toLowerCase().includes(q)
    );

    this.paginationOrdersNot.total = filtered.length;
    this.paginationOrdersNot.start = this.paginationOrdersNot.page * this.paginationOrdersNot.size;
    const end = this.paginationOrdersNot.start + this.paginationOrdersNot.size;
    this.paginationOrdersNot.end = Math.min(end, filtered.length);

    this.ordersNotAssociated = filtered.slice(this.paginationOrdersNot.start, this.paginationOrdersNot.end);
    this.drawMultipleLinks();
  }

  public refreshOrdersYesPagination() {
    const q = (this.filterOrdersYes || '').toLowerCase();
    const filtered = this.allOrdersAssociated.filter(o =>
      (o.number || '').toLowerCase().includes(q) ||
      (o.providerName || '').toLowerCase().includes(q)
    );

    this.paginationOrdersYes.total = filtered.length;
    this.paginationOrdersYes.start = this.paginationOrdersYes.page * this.paginationOrdersYes.size;
    const end = this.paginationOrdersYes.start + this.paginationOrdersYes.size;
    this.paginationOrdersYes.end = Math.min(end, filtered.length);

    this.ordersAssociated = filtered.slice(this.paginationOrdersYes.start, this.paginationOrdersYes.end);
    this.drawMultipleLinks();
  }

  // =================== Paginación / filtros (compras) ===================
  private timeoutPurchases: any = null;
  debouncedFilterPurchases() {
    clearTimeout(this.timeoutPurchases);
    this.timeoutPurchases = setTimeout(() => {
      this.paginationPurchases.page = 0;
      this.refreshPurchasesPagination();
    }, 250);
  }

  refreshPurchasesPagination() {
    const q = (this.filterPurchases || '').toLowerCase();
    const matches = (p: PurchaseVM) =>
      (p.number || '').toLowerCase().includes(q) ||
      (p.providerName || '').toLowerCase().includes(q);

    const associatedSet = new Set(this.currentRelatedPurchaseIds);

    const pinned = this.allPurchases.filter(p => associatedSet.has(p.id));
    const restRaw = this.allPurchases.filter(p => !associatedSet.has(p.id) && matches(p));

    // Prioriza "sin asociación" para que aparezcan más arriba en la lista restante
    const score = (x: PurchaseVM) => (!x.associatedOrderId ? 0 : 1);
    const rest = restRaw.sort((a, b) => score(a) - score(b));

    const final = [...pinned, ...rest];

    this.paginationPurchases.total = final.length;
    this.paginationPurchases.start = this.paginationPurchases.page * this.paginationPurchases.size;
    const end = this.paginationPurchases.start + this.paginationPurchases.size;
    this.paginationPurchases.end = Math.min(end, final.length);

    this.purchases = final.slice(this.paginationPurchases.start, this.paginationPurchases.end);
    this.drawMultipleLinks();
  }

  // =================== Utilidades ===================
  private resetAllPurchasesToBase() {
    this.allPurchases = this.basePurchases.map(p => ({ ...p }));
  }

  private upsertAssociatedIntoAllPurchases(rawAssociated: any[], orderId: number) {
    const normalized: PurchaseVM[] = (rawAssociated || []).map(p => {
      const vm = this.normalizePurchase(p);
      vm.associatedOrderId = orderId;
      return vm;
    });

    this.associatedPurchasesCache.set(orderId, normalized);

    const byId = new Map<number, PurchaseVM>();
    for (const p of this.allPurchases) byId.set(p.id, p);
    for (const p of normalized) byId.set(p.id, p);

    this.allPurchases = Array.from(byId.values());
  }

  private reloadOrdersOnly() {
    forkJoin({
      not: this.purchaseOrderService.getOrdersNotAssociated(),
      yes: this.purchaseOrderService.getOrdersAssociated(),
    }).subscribe({
      next: ({ not, yes }: any) => {
        this.allOrdersNotAssociated = (not?.data || []).map((o: any) => this.normalizeOrder(o))
          .sort((a, b) => Number(b.number) - Number(a.number));
        this.allOrdersAssociated = (yes?.data || []).map((o: any) => this.normalizeOrder(o))
          .sort((a, b) => Number(b.number) - Number(a.number));
        this.refreshOrdersNotPagination();
        this.refreshOrdersYesPagination();
      },
      error: () => this.toastService.error('No se pudieron actualizar las listas de órdenes', 'Error')
    });
  }

  // SVG (sin cambios)
  drawMultipleLinks() {
    const svg = this.svgContainer?.nativeElement as SVGElement;
    if (!svg) return;

    svg.innerHTML = '';
    if (!this.selectedOrderId || this.currentRelatedPurchaseIds.length === 0) return;

    setTimeout(() => {
      const parentRect = (svg.parentElement as HTMLElement).getBoundingClientRect();
      const selectedRow = document.querySelector('#table-orders-not tr.selected, #table-orders-yes tr.selected') as HTMLElement;
      if (!selectedRow) return;

      const selectedRect = selectedRow.getBoundingClientRect();
      const x1 = selectedRect.right - parentRect.left - 12;
      const y1 = selectedRect.top + selectedRect.height / 2 - parentRect.top;

      for (const pid of this.currentRelatedPurchaseIds) {
        const relatedRow = document.querySelector(`#table-purchases tr[data-id="${pid}"]`) as HTMLElement;
        if (!relatedRow) continue;

        const rect = relatedRow.getBoundingClientRect();
        const x2 = rect.left - parentRect.left - 12;
        const y2 = rect.top + rect.height / 2 - parentRect.top;
        const xMiddle = x1 + ((x2 - x1) / 2);

        this.drawLine(svg, x1, xMiddle, y1, y1);
        this.drawLine(svg, xMiddle, x2, y2, y2);
        this.drawLine(svg, xMiddle, xMiddle, y1, y2);
      }
    }, 50);
  }

  private drawLine(svg: SVGElement, x1: number, x2: number, y1: number, y2: number) {
    const line = document.createElementNS('http://www.w3.org/2000/svg', 'line');
    line.setAttribute('x1', String(x1));
    line.setAttribute('y1', String(y1));
    line.setAttribute('x2', String(x2));
    line.setAttribute('y2', String(y2));
    line.setAttribute('stroke', '#001f54');
    line.setAttribute('stroke-width', '2');
    svg.appendChild(line);
  }
}
